import { createContext, useContext, type ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { AuthContext, type AppRole, type AuthContextType } from "@/hooks/auth-context";
import {
  PREVIEW_COACH_NAME,
  PREVIEW_LEADER_INITIALS,
  PREVIEW_LEADER_NAME,
} from "@/data/leaderPreviewData";

export const PREVIEW_LEADER_USER_ID = "preview-leader-user";
export const PREVIEW_LEADER_EMAIL = "alex.rivera.preview@coachveera.com";

type LeaderPreviewContextValue = {
  enabled: boolean;
  leaderName: string;
  leaderInitials: string;
  coachName: string;
  mapLeaderPathToPreview: (path: string) => string;
};

const PREVIEW_ROUTE_MAP: Array<[string, string]> = [
  ["/today", "/preview/today"],
  ["/moments", "/preview/schedule"],
  ["/insights", "/preview/insights"],
  ["/veera", "/preview/veera"],
  ["/leader/coach", "/preview/coach"],
  ["/shared-memory", "/preview/throughline"],
  ["/settings", "/preview/today"],
  ["/apps", "/preview/schedule"],
  ["/help", "/preview/today"],
];

const previewUser = {
  id: PREVIEW_LEADER_USER_ID,
  email: PREVIEW_LEADER_EMAIL,
  app_metadata: {},
  user_metadata: {
    full_name: PREVIEW_LEADER_NAME,
  },
  aud: "authenticated",
  created_at: "2026-04-03T00:00:00.000Z",
} as User;

const previewSession = {
  access_token: "preview-access-token",
  refresh_token: "preview-refresh-token",
  expires_in: 3600,
  expires_at: Math.floor(Date.now() / 1000) + 3600,
  token_type: "bearer",
  user: previewUser,
} as Session;

const noopAuthResult = async () => ({ error: null });
const noopSignOut = async () => {
  // No-op: preview mode has no real session to sign out.
  // The "Close preview" affordance in LeaderLayout calls window.close() directly.
};
const noopEnsureProfile = async () => true;
const noopRefreshRoles = async () => {};

const previewAuthValue: AuthContextType = {
  user: previewUser,
  session: previewSession,
  loading: false,
  roles: ["leader" satisfies AppRole],
  signUp: noopAuthResult,
  signIn: noopAuthResult,
  signInWithGoogle: noopAuthResult,
  signInWithLinkedIn: noopAuthResult,
  signInWithAzure: noopAuthResult,
  signOut: noopSignOut,
  ensureProfile: noopEnsureProfile,
  refreshRoles: noopRefreshRoles,
};

const LeaderPreviewContext = createContext<LeaderPreviewContextValue | null>(null);

function mapLeaderPathToPreview(path: string) {
  const normalized = path.startsWith("/") ? path : `/${path}`;
  const match = PREVIEW_ROUTE_MAP.find(([leaderPath]) => (
    normalized === leaderPath || normalized.startsWith(`${leaderPath}/`)
  ));

  if (!match) {
    return "/preview/today";
  }

  const [leaderBase, previewBase] = match;
  return normalized === leaderBase
    ? previewBase
    : normalized.replace(leaderBase, previewBase);
}

export function PreviewLeaderProvider({ children }: { children: ReactNode }) {
  return (
    <LeaderPreviewContext.Provider
      value={{
        enabled: true,
        leaderName: PREVIEW_LEADER_NAME,
        leaderInitials: PREVIEW_LEADER_INITIALS,
        coachName: PREVIEW_COACH_NAME,
        mapLeaderPathToPreview,
      }}
    >
      <AuthContext.Provider value={previewAuthValue}>
        {children}
      </AuthContext.Provider>
    </LeaderPreviewContext.Provider>
  );
}

export function useLeaderPreview() {
  const context = useContext(LeaderPreviewContext);
  return context ?? {
    enabled: false,
    leaderName: "",
    leaderInitials: "",
    coachName: "",
    mapLeaderPathToPreview: (path: string) => path,
  };
}
